import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { Fixture } from '../../services/TennisLiveApi';

interface MatchTileProps {
  fixture: Fixture;
  onClick?: () => void;
}

export const MatchTile: React.FC<MatchTileProps> = ({ fixture, onClick }) => {
  const [expanded, setExpanded] = useState(false);

  const isLive = fixture.status.includes('🔴');

  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const mins = String(date.getMinutes()).padStart(2, '0');
    return ` • ${day}/${month} ${hours}:${mins}`;
  };

  return (
    <div className="gr-match-tile">
      <div 
        onClick={() => { if (onClick) onClick(); }}
        style={onClick ? { cursor: 'pointer' } : {}}
      >
        {/* Header */}
        <div className="gr-match-header">
          <span className="gr-match-tournament">
            {fixture.tournamentName}{formatDateTime(fixture.date)}
          </span>
          <div className={`gr-match-category-tag ${fixture.category.toLowerCase()}`}>
            <span className={`gr-match-category-text ${fixture.category.toLowerCase()}`}>
              {fixture.category}
            </span>
          </div>
        </div>

        {/* Body: Scoreboard */}
        <div className="gr-match-body">
          {/* Status / Live Badge at Top */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '12px', fontWeight: 'bold' }}>
            <span style={{ color: isLive ? '#ef4444' : '#94a3b8', display: 'flex', alignItems: 'center', gap: '6px' }}>
              {isLive && <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#ef4444' }} />}
              {fixture.status.replace('🔴 ', '')}
            </span>
          </div>

          {/* Player 1 */}
          <div className="gr-player-row">
            <div className="gr-player-info" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {Array.isArray(fixture.homePlayer.country) ? (
                fixture.homePlayer.country.map((flag, idx) => {
                  const nameParts = fixture.homePlayer.name.split('/');
                  return (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {flag && <img src={flag} alt="flag" style={{ width: '16px', height: '11px', objectFit: 'cover', borderRadius: '2px', border: '1px solid #cbd5e1' }} />}
                      <span className="gr-player-name">{nameParts[idx]?.trim()}</span>
                    </div>
                  );
                })
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {fixture.homePlayer.country && (
                    <img src={fixture.homePlayer.country as string} alt="flag" style={{ width: '16px', height: '11px', objectFit: 'cover', borderRadius: '2px', border: '1px solid #cbd5e1' }} />
                  )}
                  <span className="gr-player-name">{fixture.homePlayer.name}</span>
                </div>
              )}
            </div>
            <div className="gr-player-score-container" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {fixture.score.home.map((setScore, index) => (
                <span key={`h-${index}`} className="gr-player-score">
                  {setScore}
                </span>
              ))}
              {fixture.score.liveGameScore && fixture.score.liveGameScore.includes('-') && (
                <span style={{ color: '#ef4444', fontWeight: 'bold', fontSize: '14px', minWidth: '20px', textAlign: 'right' }}>
                  {fixture.score.liveGameScore.split('-')[0].trim()}
                </span>
              )}
            </div>
          </div>

          {/* Player 2 */}
          <div className="gr-player-row">
            <div className="gr-player-info" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {Array.isArray(fixture.awayPlayer.country) ? (
                fixture.awayPlayer.country.map((flag, idx) => {
                  const nameParts = fixture.awayPlayer.name.split('/');
                  return (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {flag && <img src={flag} alt="flag" style={{ width: '16px', height: '11px', objectFit: 'cover', borderRadius: '2px', border: '1px solid #cbd5e1' }} />}
                      <span className="gr-player-name">{nameParts[idx]?.trim()}</span>
                    </div>
                  );
                })
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {fixture.awayPlayer.country && (
                    <img src={fixture.awayPlayer.country as string} alt="flag" style={{ width: '16px', height: '11px', objectFit: 'cover', borderRadius: '2px', border: '1px solid #cbd5e1' }} />
                  )}
                  <span className="gr-player-name">{fixture.awayPlayer.name}</span>
                </div>
              )}
            </div>
            <div className="gr-player-score-container" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {fixture.score.away.map((setScore, index) => (
                <span key={`a-${index}`} className="gr-player-score">
                  {setScore}
                </span>
              ))}
              {fixture.score.liveGameScore && fixture.score.liveGameScore.includes('-') && (
                <span style={{ color: '#ef4444', fontWeight: 'bold', fontSize: '14px', minWidth: '20px', textAlign: 'right' }}>
                  {fixture.score.liveGameScore.split('-')[1].trim()}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Expand Indicator & Stats Area */}
      {fixture.liveStats && (
        <>
          <div 
            className="gr-match-expand"
            onClick={() => setExpanded(!expanded)}
            style={{ cursor: 'pointer', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: 8, paddingBottom: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}
          >
            <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#64748b', marginBottom: '4px' }}>ESTATÍSTICAS DA PARTIDA</span>
            {expanded ? <ChevronUp size={16} color="#cbd5e1" /> : <ChevronDown size={16} color="#cbd5e1" />}
          </div>
          
          {expanded && (
            <div className="gr-match-stats">
              <div className="gr-stats-columns">
                <span className="gr-col-player text-left">{fixture.homePlayer.name}</span>
                <span className="gr-col-label"></span>
                <span className="gr-col-player text-right">{fixture.awayPlayer.name}</span>
              </div>
              <StatRow 
                label="Aces" 
                home={fixture.liveStats.homeAces} 
                away={fixture.liveStats.awayAces} 
              />
              <StatRow 
                label="Duplas Faltas" 
                home={fixture.liveStats.homeDoubleFaults} 
                away={fixture.liveStats.awayDoubleFaults} 
                reverseColors
              />
              <StatRow 
                label="Quebras de Saque" 
                home={fixture.liveStats.homeBreakPoints} 
                away={fixture.liveStats.awayBreakPoints} 
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

const StatRow = ({ label, home, away, reverseColors = false }: { label: string; home: number; away: number; reverseColors?: boolean }) => {
  const homeBetter = reverseColors ? home < away : home > away;
  const awayBetter = reverseColors ? away < home : away > home;
  const tie = home === away;

  return (
    <div className="gr-stat-row">
      <span className={`gr-stat-value ${homeBetter ? 'better' : (tie ? 'tie' : 'worse')}`}>
        {home}
      </span>
      <span className="gr-stat-label">{label}</span>
      <span className={`gr-stat-value ${awayBetter ? 'better' : (tie ? 'tie' : 'worse')}`}>
        {away}
      </span>
    </div>
  );
};
